import { Fragment } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { Asset } from '@opallabs/kuiper'
import { DragAndDropAssetGallery } from '../../../../../DragAndDropAssetGallery'
import { twitterTweetPositions } from '../../../../../DragAndDropAssetGallery/positions'
import { LinkPreviewAssetCarousel } from '../../../../../LinkPreviewAssetCarousel'
import {
  twitterTweetDesktopFields,
  twitterTweetMobileFields,
} from '../../../../../LinkPreviewAssetCarousel/fields'
import { useContentWithLinkPreviewAssetCarousel } from '../../../../helpers/hooks'
import {
  LinkWithData,
  Module,
  OnLinkDataUpdate,
  OnShowAssetPickerProps,
} from '../../../../helpers/types'
import { TwitterStreamFooter } from '../../../sharedDisplays/footers/TwitterStreamFooter'
import { TwitterStreamHeader } from '../../../sharedDisplays/headers/TwitterStreamHeader'
import { styles } from './styles'

export interface Props {
  accountImage: AssetReference | null
  accountName: string
  accountUsername: string
  assets: AssetReference[]
  content: string
  display: string
  index: number
  isAccountPremium?: boolean
  isPresenting: boolean
  isSnapshot: boolean
  isThreaded: boolean
  linksWithData: LinkWithData[]
  modules: Module[]
  onAssetChange: () => void
  onContentChange: (val: string) => void
  onExpandAsset: (assetId: number) => void
  onLinkDataUpdate: OnLinkDataUpdate
  onRemoveAsset: (assetId: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  postType: string
  readOnly: boolean
  updateCaption: () => void
}

export const Stream: React.FC<Props> = ({
  accountImage,
  accountName,
  accountUsername,
  assets,
  content,
  display = 'mobileStream',
  index,
  isAccountPremium,
  isPresenting = false,
  isThreaded,
  linksWithData,
  modules,
  onAssetChange,
  onContentChange,
  onExpandAsset,
  onLinkDataUpdate,
  onRemoveAsset,
  onShowAssetPicker,
  postType,
  readOnly,
  updateCaption,
}) => {
  const isMobile = display.includes('mobile')
  const modulesLength = modules ? modules.length : 0

  // Only show link preview if this module is a link preview module.
  // For instance, image modules should not show a link preview even if
  // the content contains a link.
  const isLinkPreviewModule =
    modules?.[index]?.componentName === 'twitter_thread_link_preview'

  const { link, isPersistingLink, setIsPersistingLink, linkWithData } =
    useContentWithLinkPreviewAssetCarousel({
      content,
      linksWithData,
      enabled: isLinkPreviewModule,
    })
  const linkAssets = linkWithData ? linkWithData.assetReferences : []

  return (
    <Fragment>
      <TwitterStreamHeader
        accountImage={accountImage}
        accountName={accountName}
        accountUsername={accountUsername}
        content={content}
        index={index}
        isAccountPremium={isAccountPremium}
        isMobile={isMobile}
        isThreaded={isThreaded}
        modulesLength={modulesLength}
        onContentChange={onContentChange}
        postType={postType}
        readOnly={readOnly}
      />
      {assets.length === 0 && isPersistingLink && isLinkPreviewModule && (
        <div css={styles.getContentWrapper(isMobile)}>
          <LinkPreviewAssetCarousel
            assetStyle={styles.getLinkAssetStyle(
              linkAssets.length > 0,
              display
            )}
            disableRemoveLink={postType === 'thread'}
            descriptionStyle={styles.getLinkDescriptionStyle(isMobile)}
            domainStyle={styles.getLinkDomainStyle(isMobile)}
            fields={
              isMobile ? twitterTweetMobileFields : twitterTweetDesktopFields
            }
            hasRemovedLink={() => setIsPersistingLink(false)}
            hasSquareInlineAsset={false}
            link={link}
            linkStyle={styles.getLinkStyle(linkAssets.length > 0, display)}
            linkWithData={linkWithData}
            onLinkDataUpdate={onLinkDataUpdate}
            onShowAssetPicker={onShowAssetPicker}
            readOnly={readOnly || isPresenting}
            titleStyle={styles.linkTitleStyle}
          />
        </div>
      )}
      {assets.length === 1 && (
        <div css={styles.getContentWrapper(isMobile)}>
          <Asset
            assetReference={assets[0]}
            hasAssetOptions={true}
            hasAssetOverlay={assets[0] && (!readOnly || isPresenting)}
            imageFit="cover"
            onEmbiggen={(): void => onExpandAsset(assets[0].id || 0)}
            onRemove={(): void => onRemoveAsset(assets[0].id || 0)}
            overrideStyles={styles.getAssetStyles(assets[0], isMobile)}
            placeholder={true}
            removeable={!isPresenting}
            updateCaption={updateCaption}
          />
        </div>
      )}
      {assets.length > 1 && (
        <div css={styles.getContentWrapper(isMobile)}>
          <DragAndDropAssetGallery
            assets={assets}
            display={display}
            galleryContainerStyles={styles.getGalleryContainer(display)}
            hasAssetOptions={true}
            isPresenting={isPresenting}
            maxAssets={4}
            maxHeight={isMobile ? 159 : 287}
            maxWidth={isMobile ? 283 : 510}
            onAssetChange={onAssetChange}
            onExpandAsset={onExpandAsset}
            onRemoveAsset={onRemoveAsset}
            positions={twitterTweetPositions}
            readOnly={readOnly}
            updateCaption={updateCaption}
          />
        </div>
      )}
      <TwitterStreamFooter isMobile={isMobile} />
    </Fragment>
  )
}
